import React, { useEffect, useMemo, useRef, useState } from "react";
import ShopMatchCard from "./ShopMatchCard";
import { buildShopMapPopupHtml, createShopMapPinElement } from "./shopMapPopupCard";
import { buildShopPopupViewModel, getShopGoogleMapsUrl } from "./shopDisplayHelpers";
import { mapStyleUrl } from "../utils/theme";
import { API_BASE } from "../utils/apiBase";

const MAPBOX_JS_URL = "https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.js";
const MAPBOX_CSS_URL = "https://api.mapbox.com/mapbox-gl-js/v3.12.0/mapbox-gl.css";

function loadMapboxAssets() {
  if (typeof window === "undefined") return Promise.reject(new Error("No window"));
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);

  return new Promise((resolve, reject) => {
    const existingCss = document.querySelector(`link[href="${MAPBOX_CSS_URL}"]`);
    if (!existingCss) {
      const css = document.createElement("link");
      css.rel = "stylesheet";
      css.href = MAPBOX_CSS_URL;
      document.head.appendChild(css);
    }

    const existingScript = document.querySelector(`script[src="${MAPBOX_JS_URL}"]`);
    if (existingScript) {
      if (window.mapboxgl) {
        resolve(window.mapboxgl);
        return;
      }
      existingScript.addEventListener("load", () => resolve(window.mapboxgl), { once: true });
      existingScript.addEventListener("error", () => reject(new Error("Failed to load Mapbox script")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = MAPBOX_JS_URL;
    script.async = true;
    script.onload = () => resolve(window.mapboxgl);
    script.onerror = () => reject(new Error("Failed to load Mapbox script"));
    document.body.appendChild(script);
  });
}


export default function ShopPreviewPage({ token, onSessionExpired }) {
  const runtimeToken =
    typeof window !== "undefined" && window.__ENV__ && typeof window.__ENV__.REACT_APP_MAPBOX_TOKEN === "string"
      ? window.__ENV__.REACT_APP_MAPBOX_TOKEN
      : "";
  const mapToken = runtimeToken || process.env.REACT_APP_MAPBOX_TOKEN || "";
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapboxRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [shop, setShop] = useState(null);
  const [explorerShop, setExplorerShop] = useState(null);
  const [mapError, setMapError] = useState("");
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        if (res.status === 401 || res.status === 403) {
          if (onSessionExpired) onSessionExpired("/login");
          return;
        }
        if (!res.ok || !active) return;
        const data = await res.json();
        if (!active) return;
        setShop(data);
        const explorerRes = await fetch(`${API_BASE}/messages/shops/explorer?include_self=true`, {
          headers: { Authorization: `Bearer ${token}` },
          cache: "no-store",
        });
        if (explorerRes.status === 401 || explorerRes.status === 403) {
          if (onSessionExpired) onSessionExpired("/login");
          return;
        }
        if (!explorerRes.ok || !active) return;
        const explorerData = await explorerRes.json();
        if (!active || !Array.isArray(explorerData)) return;
        const selfShop = explorerData.find((item) => item?.id === data?.id);
        setExplorerShop(selfShop || null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token, onSessionExpired]);

  const mapShop = useMemo(() => {
    if (!shop) return null;
    return {
      ...shop,
      ...explorerShop,
      does_general_repair:
        explorerShop?.does_general_repair !== undefined
          ? explorerShop.does_general_repair
          : Boolean(shop.shop_general_repair),
    };
  }, [shop, explorerShop]);

  const mapsUrl = useMemo(() => (mapShop ? getShopGoogleMapsUrl(mapShop) : ""), [mapShop]);

  const showInactiveWarning = Boolean(shop?.is_shop && !shop?.shop_subscription_active);

  useEffect(() => {
    if (!mapToken || !mapShop?.is_shop || !mapContainerRef.current || mapRef.current) return undefined;

    let active = true;
    loadMapboxAssets()
      .then((mapboxgl) => {
        if (!active || !mapContainerRef.current || mapRef.current) return;
        mapboxRef.current = mapboxgl;
        mapboxgl.accessToken = mapToken;
        const startCenter = [
          Number.isFinite(mapShop.longitude) ? mapShop.longitude : -98.5795,
          Number.isFinite(mapShop.latitude) ? mapShop.latitude : 39.8283,
        ];
        const map = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: mapStyleUrl(),
          center: startCenter,
          zoom: Number.isFinite(mapShop.latitude) && Number.isFinite(mapShop.longitude) ? 9 : 3,
        });
        map.addControl(new mapboxgl.NavigationControl(), "top-right");
        mapRef.current = map;
        setMapReady(true);
      })
      .catch(() => {
        if (active) setMapError("Failed to load Mapbox.");
      });

    return () => {
      active = false;
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      setMapReady(false);
    };
  }, [mapToken, mapShop]);

  useEffect(() => {
    const map = mapRef.current;
    const mapboxgl = mapboxRef.current;
    if (!map || !mapboxgl || !mapShop?.is_shop || !mapReady) return;

    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    if (!Number.isFinite(mapShop.latitude) || !Number.isFinite(mapShop.longitude)) return;

    const popupView = buildShopPopupViewModel(mapShop, { mapsUrl });
    const popupHtml = buildShopMapPopupHtml({
      shop: mapShop,
      mapsUrl: popupView.mapsUrl,
      badges: popupView.badges,
      trustSummary: popupView.trustSummary,
    });

    const marker = new mapboxgl.Marker({
      element: createShopMapPinElement({ variant: mapShop.is_mobile_mechanic ? "mobile" : "shop" }),
    })
      .setLngLat([mapShop.longitude, mapShop.latitude])
      .setPopup(new mapboxgl.Popup({ offset: 20, maxWidth: "640px", className: "shop-map-popup shop-map-popup--flush-close" }).setHTML(popupHtml))
      .addTo(map);
    marker.togglePopup();
    markerRef.current = marker;

    map.flyTo({
      center: [mapShop.longitude, mapShop.latitude],
      zoom: 10,
    });
  }, [mapShop, mapsUrl, mapReady]);

  if (loading) {
    return <div className="max-w-2xl mx-auto p-4 text-sm text-gray-600 dark:text-gray-300">Loading shop preview…</div>;
  }

  if (!shop?.is_shop) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="border rounded-xl px-3 py-2 text-sm border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200">
          This preview is available for shop users.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-4">
      <div className="border rounded-xl bg-white dark:bg-gray-800 shadow-sm p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Shop preview</h3>
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
          This page lets you preview how your shop appears to car owners when they browse shops on the car request page.
        </p>
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
          Use this view to quickly confirm your shop name, location, links, and specialization tags are accurate.
        </p>
      </div>

      {showInactiveWarning ? (
        <div className="border rounded-xl px-3 py-2 text-sm border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-gray-900 text-yellow-900 dark:text-yellow-200">
          Your shop is currently not visible to other users, please update your subscription under your profile settings.
        </div>
      ) : null}

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">How your shop card appears:</p>
        {mapShop ? (
          <ShopMatchCard shop={mapShop} mapsUrl={mapsUrl} />
        ) : null}
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-200">How your shop appears on the map:</p>
        {!mapToken ? (
          <div className="border rounded-xl px-3 py-2 text-sm border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-gray-900 text-yellow-900 dark:text-yellow-200">
            Shop map preview needs a Mapbox token. Set <code>REACT_APP_MAPBOX_TOKEN</code> in your frontend environment.
          </div>
        ) : null}
        {mapError ? (
          <div className="border rounded-xl px-3 py-2 text-sm border-red-300 bg-red-50 dark:bg-red-950/40 text-red-800 dark:text-red-300">{mapError}</div>
        ) : null}
        {!Number.isFinite(mapShop?.latitude) || !Number.isFinite(mapShop?.longitude) ? (
          <div className="border rounded-xl px-3 py-2 text-sm border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/40 text-blue-900 dark:text-blue-200">
            We couldn't place your address on the map yet - double-check your street address and ZIP in Shop Profile.
          </div>
        ) : null}
        {mapToken ? (
          <div className="border rounded-xl overflow-hidden">
            <div ref={mapContainerRef} className="w-full h-[420px]" />
          </div>
        ) : null}
      </div>
    </div>
  );
}
